// src/components/onboarding/OnboardingPlanModal.jsx
//
// Stage A review — the contract and its service lines, from the STRUCTURED
// half of POST /api/onboarding/plan ({ plan }), shown before anything is
// written. Replaces the read-only <pre> dump.
//
// ⛔ WHAT IS EDITABLE, AND WHY THE REST IS NOT. Editable: the contract NAME (a
// label) and, per service line, include/exclude and QUANTITY — the control
// that earns the modal; a line Craig does not want should not reach Autotask.
// Display-only, deliberately:
//   setupFee 0        — setup is billed on the Stage D Setup Fees QUOTE; a fee
//                       here as well charges the client TWICE.
//   start / end date  — from contractDates(signedAt), the EARLIEST signature
//                       across the proposal's signed contracts. If the date is
//                       wrong the SIGNATURE is wrong — fix it there, or the
//                       contract silently diverges from the signature record.
//   contractType 7 + setupFee together — Autotask 500s otherwise. Not a knob.
//   SLA, period type  — derived from the tier; changing the tier is a proposal
//                       edit, not a push-time edit.
// The server enforces the same list (onboardingService.applyOverrides); this
// modal posts back only { contractName, excludeServiceLines, serviceLineQuantities }.
//
// ⚠ SURFACED, NOT BURIED: blockers (Stage A cannot run → confirm disabled, no
// 409 to press into), problems[] (the dry run exists to put these in front of
// a human — never collapsed), and the baseline confirmation (opt-OUT only, and
// never silently: it is the one assumption that could not be proven read-only).
import React, { useEffect, useState } from "react";
import { reviewedMonthlyTotal, stageAOverrides } from "./onboardingReview";
import { stepForBackendCode, stepTitle } from "./postWonSteps";

/* The heading comes from the display map: this modal runs BACKEND stage "A",
   which is shown as Stage B — Contracts for Autotask. Never hardcode the letter. */
const TITLE = stepTitle(stepForBackendCode("A"));

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const day = (iso) => (iso ? String(iso).slice(0, 10) : "—");

export default function OnboardingPlanModal({ plan, busy, reviewOnly, onCancel, onConfirm }) {
  const [contractName, setContractName] = useState("");
  const [excluded, setExcluded] = useState(() => new Set());
  const [quantities, setQuantities] = useState({});
  const [confirmBaseline, setConfirmBaseline] = useState(true);

  useEffect(() => {
    setContractName(plan?.stageA?.contract?.contractName || "");
    setExcluded(new Set());
    setQuantities({});
    setConfirmBaseline(true);
  }, [plan]);

  if (!plan) return null;
  const c = plan.stageA.contract;
  const lines = plan.stageA.serviceLines || [];
  const blockers = plan.blockers || [];
  const problems = plan.problems || [];
  const total = reviewedMonthlyTotal(lines, excluded, quantities);
  const includedCount = lines.filter((l) => !excluded.has(String(l.serviceID))).length;
  const canConfirm = !reviewOnly && !busy && plan.canRunStageA && blockers.length === 0;

  const toggle = (id) => setExcluded((prev) => { const n = new Set(prev); const k = String(id); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  const confirm = () => onConfirm({
    overrides: { stageA: stageAOverrides({ lines, builtName: c.contractName, contractName, excluded, quantities }) },
    confirmBaseline,
  });

  const row = { display: "flex", gap: 10, fontSize: 11, lineHeight: 1.5 };
  const k = { width: 150, color: "var(--text3,#777)", flexShrink: 0 };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(11,34,64,0.45)", zIndex: 500,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
         onClick={busy ? undefined : onCancel}>
      <div style={{ background: "var(--s1,#fff)", borderRadius: 10, padding: 22, width: 760, maxWidth: "100%",
                    maxHeight: "92vh", overflowY: "auto", border: "1px solid var(--border,#e2e8f0)" }}
           onClick={(e) => e.stopPropagation()} data-testid="onboarding-plan-modal">
        <div style={{ fontFamily: "var(--ff-head)", fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
          {TITLE} — {reviewOnly ? "review the plan" : "create the contract"} · {plan.clientName}
        </div>
        <div style={{ fontSize: 11, color: "var(--text3,#777)", marginBottom: 12, lineHeight: 1.6 }}>
          {plan.tier || "no tier"} · Autotask company {plan.companyID ? `#${plan.companyID}${plan.companyName ? ` (${plan.companyName})` : ""}` : "NOT SET"}
          · signed {day(plan.signedAt)}. Nothing is written until you press Create.
          {" "}<strong>This writes a financial record</strong>; Autotask accepts a duplicate without complaint.
        </div>

        {blockers.length > 0 && (
          <div data-testid="plan-blockers" style={{ fontSize: 11, background: "#fee2e2", border: "1px solid #fca5a5", color: "#991b1b", borderRadius: 6, padding: "8px 10px", marginBottom: 10, lineHeight: 1.5 }}>
            <strong>{TITLE} cannot run.</strong>
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>
          </div>
        )}
        {problems.length > 0 && (
          <div data-testid="plan-problems" style={{ fontSize: 11, background: "#fef3c7", border: "1px solid #fcd34d", color: "#92400e", borderRadius: 6, padding: "8px 10px", marginBottom: 10, lineHeight: 1.5 }}>
            <strong>Check before you create — {problems.length} warning{problems.length === 1 ? "" : "s"} from the dry run.</strong>
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{problems.map((p, i) => <li key={i}>{p}</li>)}</ul>
          </div>
        )}

        {/* ── The contract ─────────────────────────────────────────────── */}
        <label htmlFor="plan-contract-name" style={{ fontSize: 11, fontWeight: 600, color: "var(--text2,#555)" }}>Contract name</label>
        <input id="plan-contract-name" className="inp" aria-label="Contract name" value={contractName} disabled={!!busy || reviewOnly}
               onChange={(e) => setContractName(e.target.value)} style={{ fontSize: 12, marginBottom: 8 }} maxLength={100} />
        <div style={{ border: "1px solid var(--border,#e2e8f0)", borderRadius: 6, padding: "8px 10px", marginBottom: 12, display: "grid", gap: 3 }} data-testid="plan-contract-facts">
          <div style={row}><span style={k}>Dates</span><span><strong>{day(c.startDate)} → {day(c.endDate)}</strong> — from the earliest signature on this proposal ({day(plan.signedAt)}). If the date is wrong, the signature is wrong: fix it on the contract row, not here.</span></div>
          <div style={row}><span style={k}>Type · period · SLA</span><span>Recurring Services (type {c.contractType}) · monthly (period {c.contractPeriodType}) · SLA {c.serviceLevelAgreementID ?? "none"} — derived from the {plan.tier || "—"} tier. Changing the tier is a proposal edit.</span></div>
          <div style={row}><span style={k}>Setup fee</span><span><strong>{money(c.setupFee)}</strong> — setup is billed on the Stage D Setup Fees quote. Charging it here as well would bill the client twice. (Autotask requires the field on this contract type; zero is the correct value.)</span></div>
          <div style={row}><span style={k}>Default service-desk contract</span><span>{c.isDefaultContract ? "Yes" : "No"} — per tier.</span></div>
        </div>

        {/* ── Service lines ────────────────────────────────────────────── */}
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text2,#555)", marginBottom: 4 }}>
          Service lines <span style={{ fontWeight: 400, color: "var(--text3,#777)" }}>— untick a line to leave it off the contract; quantities are editable, rates are the signed rates</span>
        </div>
        {lines.length === 0 && <div style={{ fontSize: 11, color: "var(--text3,#777)", marginBottom: 8 }}>(no service lines)</div>}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginBottom: 6 }}>
          <tbody>
            {lines.map((l) => {
              const id = String(l.serviceID);
              const off = excluded.has(id);
              const q = quantities[id] !== undefined ? quantities[id] : String(l.unitChange);
              const qn = Number(q);
              const lineTotal = off ? 0 : (Number.isFinite(qn) && qn > 0 ? qn : l.unitChange) * l.unitPrice;
              return (
                <tr key={id} data-testid={`plan-line-${id}`} style={{ borderTop: "1px solid var(--border,#e2e8f0)", opacity: off ? 0.5 : 1 }}>
                  <td style={{ padding: "6px 4px", width: 26 }}>
                    <input type="checkbox" aria-label={`Include ${l.label}`} checked={!off} disabled={!!busy || reviewOnly} onChange={() => toggle(id)} />
                  </td>
                  <td style={{ padding: "6px 4px" }}>
                    <div style={{ fontWeight: 600, textDecoration: off ? "line-through" : "none" }}>{l.label}</div>
                    <div style={{ fontSize: 9.5, color: "var(--text3,#777)" }}>service {l.serviceID} · {l.source}</div>
                  </td>
                  <td style={{ padding: "6px 4px", width: 90, textAlign: "right" }}>
                    <input className="inp" type="text" inputMode="numeric" aria-label={`Quantity for ${l.label}`} value={q} disabled={!!busy || reviewOnly || off}
                           onChange={(e) => setQuantities((prev) => ({ ...prev, [id]: e.target.value.replace(/[^\d.]/g, "") }))}
                           style={{ fontSize: 11, marginBottom: 0, textAlign: "right", padding: "3px 6px" }} />
                  </td>
                  <td style={{ padding: "6px 4px", width: 90, textAlign: "right", color: "var(--text2,#555)" }}>× {money(l.unitPrice)}</td>
                  <td style={{ padding: "6px 4px", width: 100, textAlign: "right", fontWeight: 600 }}>{money(lineTotal)}</td>
                </tr>
              );
            })}
            <tr style={{ borderTop: "2px solid var(--border2,#cbd5e1)" }}>
              <td colSpan={4} style={{ padding: "6px 4px", textAlign: "right", fontWeight: 700 }}>Monthly total ({includedCount} of {lines.length} line{lines.length === 1 ? "" : "s"})</td>
              <td data-testid="plan-monthly-total" style={{ padding: "6px 4px", textAlign: "right", fontWeight: 700 }}>{money(total)}</td>
            </tr>
          </tbody>
        </table>

        {/* ── The baseline check — opt-OUT, never silent ───────────────── */}
        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 11, lineHeight: 1.5, margin: "6px 0 14px", cursor: reviewOnly ? "default" : "pointer" }}>
          <input type="checkbox" aria-label="Confirm the zero baseline before writing quantities" checked={confirmBaseline} disabled={!!busy || reviewOnly}
                 onChange={(e) => setConfirmBaseline(e.target.checked)} style={{ marginTop: 2 }} />
          <span><strong>Confirm the zero baseline first</strong> (recommended). After the first service line is created, read its units back and stop before writing any quantity if a fresh line does not start at 0 — the one assumption that could not be proven read-only. Untick only if you know why.</span>
        </label>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
          <span style={{ fontSize: 10, color: "var(--text3,#777)", marginRight: "auto" }}>
            {reviewOnly ? "Review only — nothing is written from this view." : "Writes ONE contract and its service lines to Autotask. Run it once."}
          </span>
          <button className="btn" style={{ fontSize: 11 }} onClick={onCancel} disabled={!!busy}>{reviewOnly ? "Close" : "Cancel"}</button>
          {!reviewOnly && (
            <button className="btn btn-strat" style={{ fontSize: 11 }} disabled={!canConfirm} onClick={confirm}
                    title={blockers.length ? `${TITLE} cannot run — see the blockers above` : undefined}>
              {busy ? "Creating…" : "Create contract in Autotask"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
